import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/auth'; // same axios instance used everywhere else
import { Button, Input, Textarea, Pill, ModalShell } from '../lib/ui';

/**
 * Delivery pauses ("blackouts").
 *
 * Michael declares a period he can't deliver. Orders are never blocked: the
 * storefront reads GET /garageFarm/delivery-blackouts/status and tells the
 * customer their order will arrive on the first round after the break.
 */

const label = { fontSize: 11, fontWeight: 700, marginBottom: 4, display: 'block', color: 'var(--muted)' };
const th = { textAlign: 'left', padding: '10px 12px', fontSize: 12, color: 'var(--muted)', borderBottom: '2px solid var(--border)' };
const td = { padding: '10px 12px', verticalAlign: 'middle', fontSize: 13 };

const SLOTS = [
  { key: 'morning', label: 'Late morning', sub: '10:30–13:00' },
  { key: 'evening', label: 'Early evening', sub: '17:30–19:00' },
  { key: 'late',    label: 'Late evening',  sub: '19:00–21:00' },
];
const SLOT_LABEL = Object.fromEntries(SLOTS.map((s) => [s.key, s.label]));

const fmt = (d) => (d ? new Date(d).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '?');

// <input type="datetime-local"> wants "YYYY-MM-DDTHH:mm" in local time
function toLocalInput(d) {
  const dt = d ? new Date(d) : new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

function phase(b) {
  const now = Date.now();
  if (!b.enabled) return { tone: 'bad', text: 'Disabled' };
  if (new Date(b.end).getTime() <= now) return { tone: 'warn', text: 'Ended' };
  if (new Date(b.start).getTime() <= now) return { tone: 'bad', text: 'Active now' };
  return { tone: 'ok', text: 'Upcoming' };
}

// ── Create / edit form ───────────────────────────────────────────────────────
function BlackoutForm({ initial, onSave, onCancel, saving }) {
  const [start, setStart] = useState(toLocalInput(initial?.start));
  const [end, setEnd]     = useState(toLocalInput(initial?.end || (initial ? undefined : Date.now() + 3 * 3600e3)));
  const [slots, setSlots] = useState(initial?.slots || []);
  const [label_, setLabel] = useState(initial?.label || '');
  const [customerMessage, setCustomerMessage] = useState(initial?.customerMessage || '');
  const [enabled, setEnabled] = useState(initial ? initial.enabled : true);
  const [err, setErr] = useState('');

  const toggleSlot = (key) =>
    setSlots((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

  const submit = () => {
    const s = new Date(start);
    const e = new Date(end);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return setErr('Please set both dates');
    if (e <= s) return setErr('End must be after start');
    setErr('');
    onSave({
      start: s.toISOString(),
      end: e.toISOString(),
      slots,
      label: label_.trim(),
      customerMessage: customerMessage.trim(),
      enabled,
    });
  };

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <span style={label}>From <span style={{ color: '#e53e3e' }}>*</span></span>
          <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
        </div>
        <div>
          <span style={label}>Until <span style={{ color: '#e53e3e' }}>*</span></span>
          <Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
      </div>

      <div>
        <span style={label}>Windows affected (none selected = all windows)</span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {SLOTS.map((s) => {
            const on = slots.includes(s.key);
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => toggleSlot(s.key)}
                style={{
                  textAlign: 'left', padding: '8px 12px', cursor: 'pointer',
                  border: `2px solid ${on ? 'var(--brand-2, #4CAF50)' : 'var(--border)'}`,
                  borderRadius: 'var(--r-lg, 8px)',
                  background: on ? 'var(--brand-2-light, #f0fff0)' : '#fff',
                }}
                aria-pressed={on}
              >
                <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{s.label}</span>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)' }}>{s.sub}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <span style={label}>Internal note</span>
        <Input value={label_} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Away Thursday evening" />
      </div>

      <div>
        <span style={label}>Message shown to customers (blank = generic)</span>
        <Textarea
          rows={2}
          value={customerMessage}
          onChange={(e) => setCustomerMessage(e.target.value)}
          placeholder="Deliveries are temporarily paused while we're away."
        />
      </div>

      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Enabled
      </label>

      {err && <div style={{ color: '#c53030', fontSize: 13, fontWeight: 600 }}>{err}</div>}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        {onCancel && <Button onClick={onCancel}>Cancel</Button>}
        <Button variant="primary" onClick={submit} disabled={saving}>
          {saving ? 'Saving…' : 'Save pause'}
        </Button>
      </div>
    </div>
  );
}

// ── Live storefront preview ──────────────────────────────────────────────────
function StorefrontPreview({ status }) {
  if (!status) return null;
  const deferred = SLOTS.filter((s) => status.windows?.[s.key]?.deferred);
  return (
    <div style={{
      padding: '10px 14px', borderRadius: 'var(--r-lg, 8px)', marginBottom: 16, fontSize: 13,
      background: deferred.length ? '#fff9e6' : '#ecfff1',
      border: '1px solid var(--border)',
    }}>
      {deferred.length ? (
        <>
          <strong>Storefront:</strong> orders still go through.{' '}
          {deferred.map((s) => `${s.label} resumes ${fmt(status.windows[s.key].delivery)}`).join('; ')}.
        </>
      ) : (
        <><strong>Storefront:</strong> all delivery windows running normally.</>
      )}
    </div>
  );
}

// ── Main tab ─────────────────────────────────────────────────────────────────
export default function DeliveryBlackoutsTab({ isAdmin }) {
  const [list, setList] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState(null);
  const [banner, setBanner] = useState('');
  const timerRef = useRef(null);

  const flash = (msg) => {
    setBanner(msg);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setBanner(''), 2600);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get('/delivery-blackouts'),
        api.get('/delivery-blackouts/status'),
      ]);
      setList(Array.isArray(r.data) ? r.data : []);
      setStatus(s.data || null);
    } catch {
      flash('Failed to load delivery pauses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleCreate = async (fields) => {
    setSaving(true);
    try {
      await api.post('/delivery-blackouts', fields);
      flash('Delivery pause created');
      setShowCreate(false);
      load();
    } catch (e) {
      flash(e?.response?.data?.error || 'Failed to create pause');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (fields) => {
    setSaving(true);
    try {
      await api.patch(`/delivery-blackouts/${editId}`, fields);
      flash('Delivery pause updated');
      setEditId(null);
      load();
    } catch (e) {
      flash(e?.response?.data?.error || 'Failed to update pause');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (b) => {
    try {
      await api.patch(`/delivery-blackouts/${b._id}`, { enabled: !b.enabled });
      load();
    } catch { flash('Failed to update'); }
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`Delete this delivery pause (${fmt(b.start)} → ${fmt(b.end)})?`)) return;
    try {
      await api.delete(`/delivery-blackouts/${b._id}`);
      flash('Deleted');
      load();
    } catch { flash('Failed to delete'); }
  };

  const editDoc = list.find((b) => b._id === editId);

  return (
    <div>
      <StorefrontPreview status={status} />

      {banner && (
        <div style={{ padding: '10px 14px', background: '#ecfff1', borderRadius: 'var(--r-lg, 8px)', marginBottom: 12, fontSize: 13 }}>
          {banner}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>
          {loading ? 'Loading…' : `${list.length} pause${list.length !== 1 ? 's' : ''}`}
        </div>
        {isAdmin && (
          <Button variant="primary" onClick={() => setShowCreate(true)}>+ Add pause</Button>
        )}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>When</th>
              <th style={th}>Windows</th>
              <th style={th}>Status</th>
              <th style={th}>Customer message</th>
              <th style={th} />
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && !loading && (
              <tr><td style={td} colSpan={5}>No delivery pauses. Deliveries run as normal.</td></tr>
            )}
            {list.map((b) => {
              const p = phase(b);
              return (
                <tr key={b._id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{fmt(b.start)}</div>
                    <div style={{ color: 'var(--muted)' }}>→ {fmt(b.end)}</div>
                    {b.label && <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 2 }}>{b.label}</div>}
                  </td>
                  <td style={td}>
                    {(!b.slots || b.slots.length === 0)
                      ? 'All windows'
                      : b.slots.map((k) => SLOT_LABEL[k] || k).join(', ')}
                  </td>
                  <td style={td}><Pill tone={p.tone}>{p.text}</Pill></td>
                  <td style={{ ...td, maxWidth: 220, color: 'var(--muted)' }}>{b.customerMessage || <em>generic</em>}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {isAdmin && (
                      <>
                        <Button onClick={() => toggleEnabled(b)} style={{ marginRight: 6 }}>
                          {b.enabled ? 'Disable' : 'Enable'}
                        </Button>
                        <Button onClick={() => setEditId(b._id)} style={{ marginRight: 6 }}>Edit</Button>
                        <Button onClick={() => handleDelete(b)}>Delete</Button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <ModalShell onClose={() => setShowCreate(false)} width="min(92vw, 560px)" ariaLabel="Add delivery pause" panelStyle={{ padding: 24 }}>
          <h3 style={{ marginTop: 0 }}>Add a delivery pause</h3>
          <BlackoutForm onSave={handleCreate} onCancel={() => setShowCreate(false)} saving={saving} />
        </ModalShell>
      )}

      {editDoc && (
        <ModalShell onClose={() => setEditId(null)} width="min(92vw, 560px)" ariaLabel="Edit delivery pause" panelStyle={{ padding: 24 }}>
          <h3 style={{ marginTop: 0 }}>Edit delivery pause</h3>
          <BlackoutForm initial={editDoc} onSave={handleEdit} onCancel={() => setEditId(null)} saving={saving} />
        </ModalShell>
      )}
    </div>
  );
}
